import { ChevronLeft, Check, Minus, Plus } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '@/context/app-context';

type FitnessGoal = 'Build Muscle' | 'Get Stronger' | 'Get Lean' | 'General Fitness';
type ExperienceLevel = 'Beginner' | 'Intermediate' | 'Advanced';
type ScheduleType = 'Weekly' | 'Rotating';
type Equipment = 'Full Gym' | 'Home Gym' | 'Dumbbells Only' | 'Bodyweight' | 'Bands' | 'Custom';

export function Onboarding() {
  const navigate = useNavigate();
  const { dispatch, state } = useApp();
  const [currentStep, setCurrentStep] = useState(1);

  // Step 1: Fitness Goal
  const [fitnessGoal, setFitnessGoal] = useState<FitnessGoal | null>(null);

  // Step 2: Experience Level
  const [experienceLevel, setExperienceLevel] = useState<ExperienceLevel | null>(null);

  // Step 3: Training Days
  const [trainingDays, setTrainingDays] = useState(4);

  // Step 4: Schedule Type
  const [scheduleType, setScheduleType] = useState<ScheduleType>('Weekly');
  const [selectedWeekDays, setSelectedWeekDays] = useState<string[]>(['Monday', 'Tuesday', 'Thursday', 'Friday']);

  // Step 5: Equipment
  const [equipment, setEquipment] = useState<Equipment[]>(['Full Gym']);

  // Step 6: Body Stats
  const [weight, setWeight] = useState('');
  const [height, setHeight] = useState('');
  const [bodyFat, setBodyFat] = useState('');

  const totalSteps = 6;

  const experienceLevels = [
    {
      level: 'Beginner' as ExperienceLevel,
      description: 'Less than 1 year of consistent training',
    },
    {
      level: 'Intermediate' as ExperienceLevel,
      description: '1-3 years of consistent training',
    },
    {
      level: 'Advanced' as ExperienceLevel,
      description: '3+ years of consistent training',
    },
  ];

  const equipmentOptions: Equipment[] = ['Full Gym', 'Home Gym', 'Dumbbells Only', 'Bodyweight', 'Bands', 'Custom'];

  const weekDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  const getSplitPreview = (days: number) => {
    const previews: { [key: number]: string } = {
      2: 'Full Body x2',
      3: 'Push / Pull / Legs',
      4: 'Upper / Lower',
      5: 'Push / Pull / Legs + Upper / Lower',
      6: 'Push / Pull / Legs (2x per week)',
    };
    return previews[days] || '';
  };

  const toggleEquipment = (item: Equipment) => {
    if (equipment.includes(item)) {
      setEquipment(equipment.filter((e) => e !== item));
    } else {
      setEquipment([...equipment, item]);
    }
  };

  const toggleWeekDay = (day: string) => {
    if (selectedWeekDays.includes(day)) {
      setSelectedWeekDays(selectedWeekDays.filter((d) => d !== day));
    } else {
      setSelectedWeekDays([...selectedWeekDays, day]);
    }
  };

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    } else {
      handleFinish();
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleFinish = () => {
    if (!fitnessGoal) return;

    // Dispatch all onboarding data
    const goalMap: { [key in FitnessGoal]: string } = {
      'Build Muscle': 'Hypertrophy',
      'Get Stronger': 'Strength',
      'Get Lean': 'Weight Loss',
      'General Fitness': 'General Fitness',
    };

    dispatch({ type: 'SET_GOAL', goal: goalMap[fitnessGoal] });
    dispatch({
      type: 'SET_BODY_STATS',
      stats: {
        weight: weight ? parseFloat(weight) : undefined,
        height: height ? parseFloat(height) : undefined,
        bodyFat: bodyFat ? parseFloat(bodyFat) : undefined,
      },
    });

    // Create weekly schedule with selected days
    if (scheduleType === 'Weekly') {
      const schedule = weekDays.map((day) => ({
        day,
        workoutId: selectedWeekDays.includes(day) ? null : null,
      }));
      dispatch({ type: 'SET_WEEKLY_SCHEDULE', schedule });
      dispatch({ type: 'SET_SCHEDULE_TYPE', scheduleType: 'weekly' });
    } else {
      dispatch({ type: 'SET_SCHEDULE_TYPE', scheduleType: 'cadence' });
      dispatch({
        type: 'SET_CADENCE_SCHEDULE',
        data: {
          restMode: 'manual',
          autoRestEvery: 3,
          rotation: [],
          currentIndex: 0,
        },
      });
    }

    navigate('/');
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return fitnessGoal !== null;
      case 2:
        return experienceLevel !== null;
      case 3:
        return true; // Always valid
      case 4:
        return scheduleType === 'Rotating' || selectedWeekDays.length > 0;
      case 5:
        return equipment.length > 0;
      case 6:
        return true; // Optional step
      default:
        return false;
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: '#18181B', width: '390px', margin: '0 auto' }}
    >
      {/* Progress Bar */}
      <div style={{ backgroundColor: '#27272A', padding: '16px 20px' }}>
        <div className="flex items-center justify-between mb-2">
          <span style={{ color: '#71717A', fontSize: '12px', fontWeight: 600 }}>
            STEP {currentStep} OF {totalSteps}
          </span>
          <span style={{ color: '#71717A', fontSize: '12px', fontWeight: 600 }}>
            {Math.round((currentStep / totalSteps) * 100)}%
          </span>
        </div>
        <div
          className="w-full rounded-full overflow-hidden"
          style={{ backgroundColor: '#3F3F46', height: '6px' }}
        >
          <div
            style={{
              width: `${(currentStep / totalSteps) * 100}%`,
              height: '100%',
              backgroundColor: '#FF6B35',
              transition: 'width 0.3s ease',
            }}
          />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-5">
        {/* Step 1: Fitness Goal */}
        {currentStep === 1 && (
          <div>
            <h1 style={{ color: '#F0F0F0', fontSize: '28px', fontWeight: 700, marginBottom: '12px' }}>
              What's your fitness goal?
            </h1>
            <p style={{ color: '#71717A', fontSize: '15px', marginBottom: '32px', lineHeight: '1.6' }}>
              This helps us tailor your program to your specific objectives.
            </p>

            <div className="grid grid-cols-2 gap-3">
              {(['Build Muscle', 'Get Stronger', 'Get Lean', 'General Fitness'] as FitnessGoal[]).map((goal) => (
                <button
                  key={goal}
                  onClick={() => setFitnessGoal(goal)}
                  className="p-4 rounded-xl flex flex-col items-center justify-center text-center"
                  style={{
                    backgroundColor: fitnessGoal === goal ? '#FF6B35' : '#27272A',
                    border: fitnessGoal === goal ? '2px solid #FF6B35' : '2px solid rgba(255,255,255,0.06)',
                    color: fitnessGoal === goal ? '#18181B' : '#F0F0F0',
                    minHeight: '100px',
                    fontWeight: 700,
                    fontSize: '14px',
                  }}
                >
                  {goal}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Experience Level */}
        {currentStep === 2 && (
          <div>
            <h1 style={{ color: '#F0F0F0', fontSize: '28px', fontWeight: 700, marginBottom: '12px' }}>
              Experience level?
            </h1>
            <p style={{ color: '#71717A', fontSize: '15px', marginBottom: '32px', lineHeight: '1.6' }}>
              We'll adjust exercise selection and training volume accordingly.
            </p>

            <div className="space-y-3">
              {experienceLevels.map(({ level, description }) => (
                <button
                  key={level}
                  onClick={() => setExperienceLevel(level)}
                  className="w-full p-5 rounded-xl text-left"
                  style={{
                    backgroundColor: experienceLevel === level ? '#FF6B35' : '#27272A',
                    border: experienceLevel === level ? '2px solid #FF6B35' : '2px solid rgba(255,255,255,0.06)',
                  }}
                >
                  <div
                    style={{
                      color: experienceLevel === level ? '#18181B' : '#F0F0F0',
                      fontSize: '18px',
                      fontWeight: 700,
                      marginBottom: '4px',
                    }}
                  >
                    {level}
                  </div>
                  <div
                    style={{
                      color: experienceLevel === level ? 'rgba(24,24,27,0.7)' : '#71717A',
                      fontSize: '13px',
                    }}
                  >
                    {description}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 3: Training Days */}
        {currentStep === 3 && (
          <div>
            <h1 style={{ color: '#F0F0F0', fontSize: '28px', fontWeight: 700, marginBottom: '12px' }}>
              Training days per week?
            </h1>
            <p style={{ color: '#71717A', fontSize: '15px', marginBottom: '32px', lineHeight: '1.6' }}>
              Choose how many days you can commit to training each week.
            </p>

            <div
              className="p-8 rounded-xl flex items-center justify-center gap-6"
              style={{ backgroundColor: '#27272A', border: '1px solid rgba(255,255,255,0.06)' }}
            >
              <button
                onClick={() => setTrainingDays(Math.max(2, trainingDays - 1))}
                disabled={trainingDays <= 2}
                className="rounded-full flex items-center justify-center"
                style={{
                  width: '56px',
                  height: '56px',
                  backgroundColor: '#3F3F46',
                  color: '#F0F0F0',
                  opacity: trainingDays <= 2 ? 0.3 : 1,
                }}
              >
                <Minus size={24} />
              </button>

              <div className="text-center">
                <div style={{ color: '#FF6B35', fontSize: '64px', fontWeight: 700, lineHeight: 1 }}>
                  {trainingDays}
                </div>
                <div style={{ color: '#71717A', fontSize: '14px', marginTop: '8px' }}>days per week</div>
              </div>

              <button
                onClick={() => setTrainingDays(Math.min(6, trainingDays + 1))}
                disabled={trainingDays >= 6}
                className="rounded-full flex items-center justify-center"
                style={{
                  width: '56px',
                  height: '56px',
                  backgroundColor: '#3F3F46',
                  color: '#F0F0F0',
                  opacity: trainingDays >= 6 ? 0.3 : 1,
                }}
              >
                <Plus size={24} />
              </button>
            </div>

            {/* Split Preview */}
            <div
              className="mt-4 p-4 rounded-lg text-center"
              style={{ backgroundColor: '#1F4428', border: '1px solid #4ADE80' }}
            >
              <div style={{ color: '#4ADE80', fontSize: '13px', fontWeight: 600 }}>
                {getSplitPreview(trainingDays)}
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Schedule Type */}
        {currentStep === 4 && (
          <div>
            <h1 style={{ color: '#F0F0F0', fontSize: '28px', fontWeight: 700, marginBottom: '12px' }}>
              Schedule type?
            </h1>
            <p style={{ color: '#71717A', fontSize: '15px', marginBottom: '32px', lineHeight: '1.6' }}>
              Choose how you want to organize your training schedule.
            </p>

            <div className="space-y-3 mb-6">
              <button
                onClick={() => setScheduleType('Weekly')}
                className="w-full p-5 rounded-xl text-left"
                style={{
                  backgroundColor: scheduleType === 'Weekly' ? '#FF6B35' : '#27272A',
                  border: scheduleType === 'Weekly' ? '2px solid #FF6B35' : '2px solid rgba(255,255,255,0.06)',
                }}
              >
                <div
                  style={{
                    color: scheduleType === 'Weekly' ? '#18181B' : '#F0F0F0',
                    fontSize: '18px',
                    fontWeight: 700,
                    marginBottom: '4px',
                  }}
                >
                  Weekly
                </div>
                <div
                  style={{
                    color: scheduleType === 'Weekly' ? 'rgba(24,24,27,0.7)' : '#71717A',
                    fontSize: '13px',
                  }}
                >
                  Train on fixed days each week (e.g., Mon/Wed/Fri)
                </div>
              </button>

              <button
                onClick={() => setScheduleType('Rotating')}
                className="w-full p-5 rounded-xl text-left"
                style={{
                  backgroundColor: scheduleType === 'Rotating' ? '#FF6B35' : '#27272A',
                  border: scheduleType === 'Rotating' ? '2px solid #FF6B35' : '2px solid rgba(255,255,255,0.06)',
                }}
              >
                <div
                  style={{
                    color: scheduleType === 'Rotating' ? '#18181B' : '#F0F0F0',
                    fontSize: '18px',
                    fontWeight: 700,
                    marginBottom: '4px',
                  }}
                >
                  Rotating
                </div>
                <div
                  style={{
                    color: scheduleType === 'Rotating' ? 'rgba(24,24,27,0.7)' : '#71717A',
                    fontSize: '13px',
                  }}
                >
                  Train on a cadence (workout A, rest, workout B, rest...)
                </div>
              </button>
            </div>

            {/* Weekly Day Toggles */}
            {scheduleType === 'Weekly' && (
              <div>
                <label
                  style={{
                    color: '#71717A',
                    fontSize: '12px',
                    fontWeight: 600,
                    display: 'block',
                    marginBottom: '10px',
                    letterSpacing: '0.5px',
                  }}
                >
                  SELECT TRAINING DAYS
                </label>
                <div className="flex flex-wrap gap-2">
                  {weekDays.map((day) => (
                    <button
                      key={day}
                      onClick={() => toggleWeekDay(day)}
                      className="rounded-full px-4 py-2"
                      style={{
                        backgroundColor: selectedWeekDays.includes(day) ? '#FF6B35' : '#27272A',
                        color: selectedWeekDays.includes(day) ? '#18181B' : '#A1A1AA',
                        fontSize: '13px',
                        fontWeight: 700,
                        border: selectedWeekDays.includes(day) ? '2px solid #FF6B35' : '2px solid transparent',
                        minHeight: '38px',
                        minWidth: '52px',
                      }}
                    >
                      {day.slice(0, 3)}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 5: Equipment */}
        {currentStep === 5 && (
          <div>
            <h1 style={{ color: '#F0F0F0', fontSize: '28px', fontWeight: 700, marginBottom: '12px' }}>
              Available equipment?
            </h1>
            <p style={{ color: '#71717A', fontSize: '15px', marginBottom: '32px', lineHeight: '1.6' }}>
              Select all equipment you have access to. You can choose multiple.
            </p>

            <div className="flex flex-wrap gap-2">
              {equipmentOptions.map((item) => (
                <button
                  key={item}
                  onClick={() => toggleEquipment(item)}
                  className="rounded-full px-4 py-2.5 flex items-center gap-2"
                  style={{
                    backgroundColor: equipment.includes(item) ? '#FF6B35' : '#27272A',
                    color: equipment.includes(item) ? '#18181B' : '#A1A1AA',
                    fontSize: '13px',
                    fontWeight: 700,
                    border: equipment.includes(item) ? '2px solid #FF6B35' : '2px solid rgba(255,255,255,0.06)',
                    minHeight: '42px',
                  }}
                >
                  {equipment.includes(item) && <Check size={16} />}
                  {item}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 6: Body Stats */}
        {currentStep === 6 && (
          <div>
            <h1 style={{ color: '#F0F0F0', fontSize: '28px', fontWeight: 700, marginBottom: '12px' }}>
              Body stats (optional)
            </h1>
            <p style={{ color: '#71717A', fontSize: '15px', marginBottom: '32px', lineHeight: '1.6' }}>
              Help us track your progress over time. You can skip this step.
            </p>

            <div className="space-y-4">
              <div>
                <label
                  style={{
                    color: '#71717A',
                    fontSize: '12px',
                    fontWeight: 600,
                    display: 'block',
                    marginBottom: '8px',
                    letterSpacing: '0.5px',
                  }}
                >
                  WEIGHT (LBS)
                </label>
                <input
                  type="number"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  placeholder="e.g. 180"
                  className="w-full rounded-lg"
                  style={{
                    backgroundColor: '#27272A',
                    color: '#F0F0F0',
                    fontSize: '16px',
                    fontWeight: 600,
                    padding: '14px 16px',
                    border: '1px solid rgba(255,255,255,0.06)',
                  }}
                />
              </div>

              <div>
                <label
                  style={{
                    color: '#71717A',
                    fontSize: '12px',
                    fontWeight: 600,
                    display: 'block',
                    marginBottom: '8px',
                    letterSpacing: '0.5px',
                  }}
                >
                  HEIGHT (CM)
                </label>
                <input
                  type="number"
                  value={height}
                  onChange={(e) => setHeight(e.target.value)}
                  placeholder="e.g. 180"
                  className="w-full rounded-lg"
                  style={{
                    backgroundColor: '#27272A',
                    color: '#F0F0F0',
                    fontSize: '16px',
                    fontWeight: 600,
                    padding: '14px 16px',
                    border: '1px solid rgba(255,255,255,0.06)',
                  }}
                />
              </div>

              <div>
                <label
                  style={{
                    color: '#71717A',
                    fontSize: '12px',
                    fontWeight: 600,
                    display: 'block',
                    marginBottom: '8px',
                    letterSpacing: '0.5px',
                  }}
                >
                  BODY FAT % (OPTIONAL)
                </label>
                <input
                  type="number"
                  value={bodyFat}
                  onChange={(e) => setBodyFat(e.target.value)}
                  placeholder="e.g. 15"
                  className="w-full rounded-lg"
                  style={{
                    backgroundColor: '#27272A',
                    color: '#F0F0F0',
                    fontSize: '16px',
                    fontWeight: 600,
                    padding: '14px 16px',
                    border: '1px solid rgba(255,255,255,0.06)',
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div
        className="p-4"
        style={{
          backgroundColor: '#27272A',
          borderTop: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        <div className="flex items-center gap-3">
          {currentStep > 1 && (
            <button
              onClick={handleBack}
              className="rounded-xl flex items-center justify-center"
              style={{
                minWidth: '56px',
                minHeight: '56px',
                backgroundColor: '#3F3F46',
                color: '#F0F0F0',
              }}
            >
              <ChevronLeft size={24} />
            </button>
          )}

          <button
            onClick={handleNext}
            disabled={!canProceed()}
            className="flex-1 rounded-xl flex items-center justify-center gap-2"
            style={{
              backgroundColor: canProceed() ? '#FF6B35' : '#3F3F46',
              color: canProceed() ? '#18181B' : '#71717A',
              padding: '16px',
              fontSize: '16px',
              fontWeight: 700,
              minHeight: '56px',
              opacity: canProceed() ? 1 : 0.5,
            }}
          >
            {currentStep === totalSteps ? 'Start Training' : 'Next'}
          </button>

          {currentStep === totalSteps && (
            <button
              onClick={() => navigate('/')}
              style={{
                color: '#71717A',
                fontSize: '14px',
                fontWeight: 600,
                minWidth: '56px',
              }}
            >
              Skip
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
